import _ from 'underscore';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Virtuoso } from 'react-virtuoso';

import { api } from '@culture/common-helpers/request';

import escapeStringRegexp from 'escape-string-regexp';
import Icon from 'components/Icon';
import Preloader from 'components/Preloader';
import { DEFAULT_LIMIT } from 'utils/helpers/constants';
import FilterItem from './components/FilterItem';

import styles from './styles.module.scss';

const MobileSelect = ({
	name,
	customChildren,
	resetOption,
	selectedValue,
	options,
	valueField,
	apiParams,
	isLoading,
	searchable,
	placeholder,
	emptyMessage,
	onChange
}) => {
	const [localVal, setLocalVal] = useState([]);
	const [searchValue, setSearchValue] = useState('');
	const [previousSearchValue, setPreviousSearchValue] = useState('');
	const [isUpdating, setIsUpdating] = useState(
		_.isEmpty(apiParams) ? false : isLoading
	);
	const [isLoadingMore, setIsLoadingMore] = useState(false);
	const [preparedOptions, setPreparedOptions] = useState(options || []);
	const isAnyOptionSelected =
		resetOption && selectedValue !== resetOption[valueField];

	const optionsIsEmpty = _.isEmpty(preparedOptions);

	const notFound = !!searchValue.length && optionsIsEmpty && !isUpdating;

	const handleSearch = (event) => {
		setPreviousSearchValue(searchValue);
		setSearchValue(event.target.value);
	};

	const fetchData = useMemo(() => {
		if (_.isEmpty(apiParams)) {
			return null;
		}

		return ({ query, cached = false, extraParams, isLoadMore = false }) => {
			const { url, affectQueryParams } = apiParams;
			if (isLoadMore) {
				setIsLoadingMore(true);
			} else {
				setIsUpdating(true);
			}
			return api(url, {
				...affectQueryParams,
				...extraParams,
				cached,
				query
			})
				.then((data) => {
					const items = data?.locales;

					if (items.length === 0 && isLoadMore) {
						return;
					}

					setPreparedOptions((prevState) =>
						_.uniq(
							isLoadMore ? [...prevState, ...items] : items,
							(item) => item._id
						)
					);
				})
				.finally(() => {
					_.delay(() => {
						setIsUpdating(false);
						setIsLoadingMore(false);
					}, 300);
				});
		};
	}, [apiParams?.affectQueryParams]);

	const fetchDataDebounced = useMemo(
		() => fetchData && _.debounce(fetchData, apiParams.debounce),
		[fetchData]
	);

	useEffect(() => {
		if (fetchData && !_(apiParams).isEmpty()) {
			fetchDataDebounced.cancel();
			fetchData({ query: '', cached: true });
		} else {
			const searchRegexp = new RegExp(escapeStringRegexp(searchValue), 'i');
			const newOptions = options.filter(({ title }) =>
				searchRegexp.test(title)
			);
			setPreparedOptions(newOptions);
		}
	}, [options]);

	useEffect(() => {
		if (searchable && searchValue) {
			fetchDataDebounced({ query: searchValue });
		}
		if (!searchValue && previousSearchValue) {
			fetchDataDebounced({ query: '' });
		}
	}, [searchValue]);

	const onSelect = ({ label, value, titleGenitive }) => {
		const data = {
			label,
			value,
			titleGenitive
		};

		if (data?.value !== resetOption?.[valueField]) {
			onChange(name, data.value);
			setLocalVal(data.value);
		} else {
			onChange(name, resetOption?.[valueField]);
			setLocalVal(resetOption?.[valueField]);
		}
	};

	useEffect(() => {
		setLocalVal(selectedValue);
	}, [selectedValue]);

	const loadMore = useCallback(
		async () =>
			!!apiParams &&
			fetchData({
				query: searchValue,
				cached: true,
				extraParams: {
					...apiParams?.affectQueryParams,
					limit: DEFAULT_LIMIT,
					offset: preparedOptions.length
				},
				isLoadMore: true
			}),
		[preparedOptions]
	);

	return (
		<div className={styles.MobileSelect}>
			{searchable && (
				<div className={styles.MobileSelect__SearchWrapper}>
					<Icon icon="search" className={styles.MobileSelect__SearchIcon} />
					<input
						className={styles.MobileSelect__SearchInput}
						placeholder={placeholder}
						onChange={handleSearch}
						value={searchValue}
					/>
				</div>
			)}
			<div className={styles.MobileSelect__FilterList}>
				{isUpdating ? (
					<Preloader className={styles.MobileSelect__Preloader} />
				) : (
					<>
						{customChildren || (
							<Virtuoso
								endReached={(index) => {
									const hasMore = (index + 1) % DEFAULT_LIMIT === 0;
									if (hasMore) {
										loadMore();
									}
								}}
								className={styles.MobileSelect__VitruosoContainer}
								components={{
									Header: () => (
										<>
											{isAnyOptionSelected ? (
												<FilterItem
													value={resetOption[valueField]}
													active={_.isEqual(
														selectedValue,
														resetOption[valueField]
													)}
													title={resetOption.title}
													name={resetOption[valueField]}
													onSelect={onSelect}
												/>
											) : null}
											{notFound ? (
												<div className={styles.MobileSelect__NotFound}>
													{emptyMessage}
												</div>
											) : null}
										</>
									),
									Footer: () =>
										isLoadingMore ? (
											<Preloader className={styles.MobileSelect__Preloader} />
										) : null
								}}
								data={preparedOptions}
								itemContent={(index, data) => {
									const { _id, title, titleGenitive } = data;
									const value = data[valueField];
									return (
										<FilterItem
											key={_id || value}
											value={value}
											name={data.name}
											title={title}
											active={_.isEqual(localVal, value)}
											titleGenitive={titleGenitive}
											onSelect={onSelect}
										/>
									);
								}}
							/>
						)}
					</>
				)}
			</div>
		</div>
	);
};

MobileSelect.propTypes = {
	name: PropTypes.string.isRequired,
	options: PropTypes.arrayOf(
		PropTypes.shape({
			title: PropTypes.string.isRequired,
			name: PropTypes.string
		})
	),
	apiParams: PropTypes.shape({
		url: PropTypes.string,
		debounce: PropTypes.number,
		affectQueryParams: PropTypes.shape({})
	}),
	selectedValue: PropTypes.node,
	onChange: PropTypes.func,
	resetOption: PropTypes.shape({
		sysName: PropTypes.string.isRequired,
		title: PropTypes.string.isRequired,
		name: PropTypes.string
	}),
	valueField: PropTypes.string,
	customChildren: PropTypes.node,
	isLoading: PropTypes.bool,
	searchable: PropTypes.bool,
	placeholder: PropTypes.string,
	emptyMessage: PropTypes.string
};

MobileSelect.defaultProps = {
	options: [],
	selectedValue: '',
	valueField: 'name',
	resetOption: { sysName: 'russia', title: 'По всей России' },
	onChange: _.noop,
	isLoading: false,
	searchable: false,
	placeholder: 'Введите для поиска...',
	emptyMessage: 'Ничего не найдено'
};

export default MobileSelect;
